import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ComputerDesktopIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../contexts/ThemeContext';

interface MobileReaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookTitle?: string;
}

export const MobileReaderModal: React.FC<MobileReaderModalProps> = ({
  isOpen,
  onClose,
  bookTitle
}) => {
  const { isDarkMode } = useTheme();

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-md rounded-2xl p-6 shadow-xl ${
              isDarkMode
                ? 'bg-slate-900 border border-slate-700/50'
                : 'bg-white border border-gray-200'
            }`}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className={`absolute top-4 right-4 p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? 'hover:bg-slate-800 text-slate-400'
                  : 'hover:bg-gray-100 text-gray-500'
              }`}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            {/* Icon with glow animation */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <motion.div
                  animate={{ opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(6, 182, 212, 0.3) 0%, transparent 70%)',
                    filter: 'blur(20px)',
                    width: '80px',
                    height: '80px',
                    top: '-8px',
                    left: '-8px',
                  }}
                />
                <div
                  className="relative w-16 h-16 rounded-2xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                    boxShadow: '0 0 30px rgba(6, 182, 212, 0.3)',
                  }}
                >
                  <ComputerDesktopIcon className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className={`text-xl font-bold text-center mb-3 ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Better on a Bigger Screen
            </h2>

            {/* Message */}
            <p className={`text-center mb-6 leading-relaxed ${
              isDarkMode ? 'text-slate-300' : 'text-gray-600'
            }`}>
              Switch to a laptop or desktop for the full reading experience — annotations, highlighting, and the AI assistant work best on a bigger screen.
            </p>

            {/* Visual hint */}
            <div className={`flex items-center justify-center gap-4 mb-6 py-3 rounded-xl ${
              isDarkMode ? 'bg-slate-800/50' : 'bg-gray-50'
            }`}>
              <div className={`flex items-center gap-2 ${isDarkMode ? 'opacity-40 text-slate-400' : 'opacity-40 text-gray-500'}`}>
                <DevicePhoneMobileIcon className="w-6 h-6" />
                <span className="text-sm">Mobile</span>
              </div>
              <div className={`text-2xl ${isDarkMode ? 'text-slate-500' : 'text-gray-300'}`}>
                &rarr;
              </div>
              <div className="flex items-center gap-2">
                <ComputerDesktopIcon className="w-6 h-6 text-cyan-500" />
                <span className="text-sm font-medium text-cyan-600">Desktop</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl font-medium text-white transition-all hover:opacity-90"
                style={{
                  background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 100%)',
                  boxShadow: '0 0 20px rgba(6, 182, 212, 0.25)',
                }}
              >
                Got it!
              </button>
              <p className={`text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                You can still browse your library and chat with Vidya on mobile.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileReaderModal;
